import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { downloadStatement } from "@/components/Statements";
import AdminArtistTools from "@/components/AdminArtistTools";

const fmt = (n) => `$${(n || 0).toFixed(2)}`;
const STATUS_CLS = { requested: "border-yellow-500 text-yellow-500", paid: "border-green-500 text-green-500", rejected: "border-red-600 text-red-500" };
const FILTERS = ["requested", "paid", "rejected", "all"];

export default function AdminPayouts() {
  const [data, setData] = useState({ payouts: [], pending_total_usd: 0, threshold_usd: 50 });
  const [filter, setFilter] = useState("requested");

  const load = () => api.get("/admin/payouts").then(({ data }) => setData(data));
  useEffect(() => { load(); }, []);

  const decide = async (p, approve) => {
    const note = window.prompt(approve ? `Mark ${fmt(p.amount_usd)} to ${p.paypal_email} as paid. Optional note for the artist:` : "Reason for rejecting (sent to the artist):") ;
    if (note === null) return;
    try {
      await api.post(`/admin/payouts/${p.id}/decide`, { approve, note: note.trim() || null });
      toast.success(approve ? "Marked paid — artist emailed" : "Payout rejected — artist emailed");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const rows = data.payouts.filter((p) => filter === "all" || p.status === filter);

  return (
    <div data-testid="admin-payouts">
      <div className="grid grid-cols-2 md:grid-cols-3 border border-zinc-800 mb-8">
        <Box n={fmt(data.pending_total_usd)} l="Awaiting Payment" accent />
        <Box n={data.payouts.filter((p) => p.status === "requested").length} l="Open Requests" />
        <Box n={fmt(data.threshold_usd)} l="Payout Minimum" />
      </div>
      <div className="flex flex-wrap gap-2 mb-6">
        {FILTERS.map((f) => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`payout-filter-${f}`}
            className={`text-xs tracking-[0.2em] uppercase px-4 py-2 border transition-colors ${filter === f ? "border-red-600 bg-red-600 text-white" : "border-zinc-800 text-zinc-400 hover:border-zinc-600"}`}>{f}</button>
        ))}
      </div>
      <AdminArtistTools onChanged={load} />
      <div className="border border-zinc-800 overflow-x-auto" data-testid="admin-payouts-table">
        <div className="min-w-[800px]">
          <div className="grid grid-cols-12 gap-4 px-6 py-3 border-b border-zinc-800 text-[10px] tracking-[0.2em] uppercase text-zinc-500 bg-zinc-950">
            <div className="col-span-3">Artist</div><div className="col-span-3">PayPal</div><div className="col-span-2">Amount</div><div className="col-span-2">Requested</div><div className="col-span-2 text-right">Actions</div>
          </div>
          {rows.length === 0 ? <div className="p-10 text-center text-zinc-500 text-sm">No payouts in this bucket.</div> : rows.map((p) => (
            <div key={p.id} className="grid grid-cols-12 gap-4 px-6 py-4 border-b border-zinc-800 last:border-b-0 row-hover items-center text-sm" data-testid={`admin-payout-${p.id}`}>
              <div className="col-span-3 truncate"><div>{p.user_display_name}</div><div className="text-[10px] text-zinc-500">{p.user_email}</div></div>
              <div className="col-span-3 font-mono text-xs text-zinc-300 truncate">{p.paypal_email}</div>
              <div className="col-span-2 font-display text-lg tracking-tight">{fmt(p.amount_usd)}</div>
              <div className="col-span-2 text-[10px] text-zinc-500 font-mono">{new Date(p.requested_at).toLocaleDateString()}{p.note && <div className="text-zinc-600 truncate" title={p.note}>“{p.note}”</div>}</div>
              <div className="col-span-2 flex justify-end gap-2">
                <button onClick={() => downloadStatement(`/admin/artists/${p.user_id}/statements/all.pdf`, `statement-${p.user_email}.pdf`).catch(() => toast.error("Could not build statement"))} data-testid={`payout-statement-${p.id}`} title="Download artist's all-time statement" className="border border-zinc-700 px-2 py-1.5 text-[10px] tracking-widest uppercase hover:border-white">PDF</button>
                {p.status === "requested" ? (<>
                  <button onClick={() => decide(p, true)} data-testid={`payout-paid-${p.id}`} className="border border-green-500 text-green-500 px-3 py-1.5 text-[10px] tracking-widest uppercase hover:bg-green-500 hover:text-white">Mark Paid</button>
                  <button onClick={() => decide(p, false)} data-testid={`payout-reject-${p.id}`} className="border border-zinc-700 px-3 py-1.5 text-[10px] tracking-widest uppercase hover:border-red-600 hover:text-red-500">Reject</button>
                </>) : <span className={`text-[9px] tracking-widest uppercase border px-1.5 py-0.5 ${STATUS_CLS[p.status]}`}>{p.status}</span>}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Box({ n, l, accent }) {
  return (
    <div className={`p-6 border-r border-zinc-800 last:border-r-0 ${accent ? "bg-red-600/5" : ""}`}>
      <div className={`font-display text-3xl tracking-tighter ${accent ? "text-red-500" : ""}`}>{n}</div>
      <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 mt-2">{l}</div>
    </div>
  );
}
